import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

/**
 * A menu that hangs off a trigger but lives at the top of the document (KEY-532).
 *
 * The pickers used to be `position: absolute` inside the card that opened them, which is fine until
 * something above them clips: the Dynamic workspace gives the card stack a fixed height and scrolls it, and
 * an overflow of anything but `visible` clips every descendant. A status list cut off at the bottom of a
 * card is not a cosmetic problem — the statuses you cannot see are the ones you cannot pick.
 *
 * So it portals to `document.body` and positions itself with `position: fixed` from the trigger's rect.
 * Fixed elements are laid out against the viewport and ignore ancestor overflow entirely, which is the
 * property being bought here.
 *
 * It follows the trigger while open: fixed coordinates are a snapshot, so scrolling the card (or the page)
 * would otherwise leave the menu behind, hanging in the wrong place.
 */
export function AnchoredMenu({
  anchorRef,
  open,
  onDismiss,
  align = "right",
  minWidth,
  zIndex = 60,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** Clicking away, or Escape. */
  onDismiss: () => void;
  /** Which edge lines up with the trigger's. */
  align?: "left" | "right";
  minWidth?: number;
  zIndex?: number;
  children: ReactNode;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null);

  // Layout effect, not effect: the menu must be in its right place on the frame it first paints, or it
  // visibly jumps from the top-left corner to where it belongs.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight ?? 0;
      const below = window.innerHeight - r.bottom;
      // Flip above when there is not room below AND there is more room above — a menu that opens off the
      // bottom of the window is the same failure as one clipped by a card.
      const flip = menuHeight > 0 && below < menuHeight + 12 && r.top > below;
      setPos({
        top: flip ? Math.max(8, r.top - menuHeight - 6) : r.bottom + 6,
        ...(align === "right" ? { right: Math.max(8, window.innerWidth - r.right) } : { left: Math.max(8, r.left) }),
      });
    };
    place();
    // Capture phase: a scroll inside the card does not bubble to window, and that is exactly the scroll
    // this needs to hear about.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, align, anchorRef]);

  // Dismiss on Escape, or on a press that lands outside both the menu and the thing that opened it.
  //
  // A press LISTENER rather than a full-viewport overlay div: an overlay has to sit above everything to
  // catch the click, which means it is also in front of the menu's own rows — it swallowed every pick, and
  // did so silently, because a menu that closes on click looks exactly like a menu that was used.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) {
        return;
      }
      onDismiss();
    };
    window.addEventListener("keydown", onKey);
    // Deferred a tick: the very press that OPENED the menu is still propagating, and would close it again.
    const timer = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(timer);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onDismiss, anchorRef]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <>
      <div
        ref={menuRef}
        style={{
          position: "fixed",
          // Hidden rather than absent until placed: it has to be in the DOM to be measured for the flip,
          // but must not paint at 0,0 first.
          visibility: pos ? "visible" : "hidden",
          top: pos?.top ?? 0,
          left: pos?.left,
          right: pos?.right,
          zIndex,
          minWidth,
          maxHeight: "min(60vh, 420px)",
          overflowY: "auto",
          background: "var(--surface)",
          border: "var(--border-w) solid var(--border)",
          borderRadius: "var(--radius)",
          boxShadow: "var(--shadow-lg)",
          padding: 6,
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
